import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { api, ApiError } from "../api/client";
import { useApi } from "../hooks/useApi";
import { useTenant } from "../app/TenantContext";
import { Icon } from "../shell/Icon";

/**
 * Stammdaten-Hub im Redesign-Look. Layout = detail-hero + segmented tabs +
 * Outlet. Index zeigt 5 Karten zu den Sub-Bereichen.
 *
 * Tabs sind Routen statt State — React Router 7 trägt die aktive Sub-Route
 * via NavLink-isActive.
 */

const SUB_TABS = [
  { to: "/stammdaten/catalog", label: "Catalog & Sortiment" },
  { to: "/stammdaten/kunden", label: "Kunden" },
  { to: "/stammdaten/konditionen", label: "Konditionen" },
  { to: "/stammdaten/tenant-config", label: "Tenant-Config" },
  { to: "/stammdaten/artikel", label: "Kundenartikel (Legacy)" },
];

export function StammdatenLayout() {
  const { tenantId } = useTenant();
  const { pathname } = useLocation();
  const isIndex = pathname === "/stammdaten" || pathname === "/stammdaten/";

  const { state } = useApi(() => api.getStammdatenSummary(tenantId), [tenantId]);
  const summary = state.status === "ok" ? state.data : null;

  return (
    <div className="screen fade-up">
      <div className="detail-hero">
        <div className="detail-hero-l">
          <div className="detail-id">
            <div className="uppercase-mini">/ STAMMDATEN</div>
            <h1 className="detail-customer">Stammdaten</h1>
            <div className="detail-meta mono">
              <span className="bv-chip">tenant · {tenantId}</span>
              {summary && (
                <span className="id-chip">
                  {summary.artikel.gesamt} Artikel ·{" "}
                  {summary.konditionen.gesamt} Konditionen
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {!isIndex && (
        <div className="tabs">
          {SUB_TABS.map((t, i) => (
            <NavLink key={t.to} to={t.to} end className="tab-link-wrap">
              {({ isActive }) => (
                <span className={`tab ${isActive ? "active" : ""}`}>
                  <span className="tab-num mono">{String(i + 1).padStart(2, "0")}</span>
                  <span>{t.label}</span>
                </span>
              )}
            </NavLink>
          ))}
        </div>
      )}

      {/*
        Sub-Pages (Catalog, Kunden, Konditionen, ArtikelLegacy, TenantConfig)
        sind im Redesign noch nicht final umgebaut — der .legacy-compat-Layer
        in screens.css übersetzt ihre Tailwind-light-Farben auf das dunkle
        Theme. Folge-Sprint REDESIGN.4 ersetzt sie durch echte Redesign-
        Komponenten (.cat-table, .kond-table etc.).
      */}
      <div className={isIndex ? undefined : "legacy-compat"}>
        <Outlet />
      </div>
    </div>
  );
}

export function StammdatenIndex() {
  const { tenantId } = useTenant();
  const { state, refetch } = useApi(
    () => api.getStammdatenSummary(tenantId),
    [tenantId]
  );

  if (state.status === "loading") {
    return (
      <section className="block">
        <div className="block-head">
          <span className="uppercase-mini">// STAMMDATEN LADEN …</span>
        </div>
      </section>
    );
  }
  if (state.status === "error") {
    return (
      <section className="block" style={{ borderColor: "oklch(0.72 0.18 25 / 0.4)" }}>
        <div className="block-head">
          <span className="uppercase-mini" style={{ color: "var(--bad)" }}>
            // BACKEND NICHT ERREICHBAR
          </span>
        </div>
        <div className="notice-body">
          <div className="mono" style={{ color: "var(--bad)" }}>
            {state.error.message}
          </div>
          <div>
            <button type="button" className="btn" onClick={refetch}>
              <Icon name="refresh" size={14} /> Nochmal versuchen
            </button>
          </div>
        </div>
      </section>
    );
  }

  const s = state.data;
  return (
    <>
      <CatalogStatusBanner tenantId={tenantId} onChanged={refetch} />
      <div className="metric-grid">
        <IndexCard
          to="/stammdaten/catalog"
          label="CATALOG & SORTIMENT"
          stat={String(s.artikel.gesamt)}
          sub="Globaler Stamm + Tenant-Sortiment"
          action="Catalog pflegen + Sortiment anpassen"
          accent
        />
        <KundenCard tenantId={tenantId} />
        <IndexCard
          to="/stammdaten/konditionen"
          label="KONDITIONSMATRIX"
          stat={String(s.konditionen.gesamt)}
          sub={
            s.konditionen.kundenIds.length > 0
              ? `${s.konditionen.kundenIds.length} Kunde${s.konditionen.kundenIds.length === 1 ? "" : "n"}`
              : "keine Konditionen"
          }
          action="Rabatte + Sonderpreise pflegen"
        />
        <IndexCard
          to="/stammdaten/tenant-config"
          label="TENANT-CONFIG"
          stat={`${s.tenant.margeProzent}%`}
          sub={s.tenant.firmaName ?? "Firma fehlt"}
          action="Marge + Firma-Stammdaten"
        />
      </div>

      <section className="block" style={{ opacity: 0.85 }}>
        <div className="block-head">
          <span className="uppercase-mini">// LEGACY</span>
          <span className="block-sep" />
          <span className="uppercase-mini" style={{ color: "var(--fg-faint)" }}>
            Vor G1-Migration
          </span>
        </div>
        <NavLink to="/stammdaten/artikel" className="source-file" style={{ display: "flex" }}>
          <div className="source-file-l">
            <div
              className="file-ico-box mono"
              style={{ color: "var(--fg-dim)" }}
            >
              .json
            </div>
            <div>
              <div className="source-name mono">kundenartikel.json</div>
              <div className="source-meta uppercase-mini">
                {s.artikel.gesamt} Artikel · {s.artikel.warengruppen.length} Warengruppen
              </div>
            </div>
          </div>
          <span className="btn ghost sm">Legacy-Editor + CSV-Import →</span>
        </NavLink>
      </section>
    </>
  );
}

function KundenCard({ tenantId }: { tenantId: string }) {
  const { state } = useApi(() => api.getKunden(tenantId), [tenantId]);
  const count = state.status === "ok" ? state.data.kunden.length : 0;
  const aktiv =
    state.status === "ok" ? state.data.kunden.filter((k) => k.active).length : 0;
  return (
    <IndexCard
      to="/stammdaten/kunden"
      label="KUNDEN"
      stat={String(count)}
      sub={`${aktiv} aktiv`}
      action="Kunden + Konditionen pflegen"
    />
  );
}

function IndexCard({
  to,
  label,
  stat,
  sub,
  action,
  accent,
}: {
  to: string;
  label: string;
  stat: string;
  sub: string;
  action: string;
  accent?: boolean;
}) {
  return (
    <NavLink
      to={to}
      className={`metric ${accent ? "acc" : ""}`}
      style={{ display: "block", textDecoration: "none", color: "inherit" }}
    >
      <div className="metric-label uppercase-mini">{label}</div>
      <div className="metric-value mono" style={{ fontSize: 28 }}>
        {stat}
      </div>
      <div className="metric-sub">{sub}</div>
      <div
        className="uppercase-mini"
        style={{ marginTop: 14, color: accent ? "var(--acc-hot)" : "var(--acc)" }}
      >
        {action} →
      </div>
      <div className="metric-deco" aria-hidden>
        <svg viewBox="0 0 80 80" width="80" height="80">
          <circle cx="40" cy="40" r="30" stroke="currentColor" strokeOpacity="0.06" fill="none" />
          <circle cx="40" cy="40" r="22" stroke="currentColor" strokeOpacity="0.1" fill="none" />
          <circle cx="40" cy="40" r="14" stroke="currentColor" strokeOpacity="0.16" fill="none" />
        </svg>
      </div>
    </NavLink>
  );
}

/**
 * Migrations-Banner für Legacy-Tenants. Versteckt sich sobald migriert.
 */
function CatalogStatusBanner({
  tenantId,
  onChanged,
}: {
  tenantId: string;
  onChanged: () => void;
}) {
  const { state, refetch } = useApi(
    () => api.getSortiment(tenantId),
    [tenantId]
  );
  const [migrating, setMigrating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    catalogAdded: number;
    catalogReused: number;
    sortimentSize: number;
  } | null>(null);

  if (state.status === "loading") return null;
  if (state.status === "error") return null;

  const { sortiment, effective, orphanRefs, legacyAvailable } = state.data;

  async function handleMigrate() {
    setMigrating(true);
    setError(null);
    try {
      const r = await api.migrateLegacy(tenantId);
      setResult({
        catalogAdded: r.catalogAdded,
        catalogReused: r.catalogReused,
        sortimentSize: r.sortimentSize,
      });
      refetch();
      onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { hint?: string; error?: string } | null;
        setError(body?.hint ?? body?.error ?? err.message);
      } else if (err instanceof Error) setError(err.message);
      else setError(String(err));
    } finally {
      setMigrating(false);
    }
  }

  if (legacyAvailable && !sortiment) {
    return (
      <section
        className="block"
        style={{
          borderColor: "oklch(0.82 0.15 75 / 0.4)",
          background: "linear-gradient(180deg, var(--warn-tint), var(--bg-base) 60%)",
        }}
      >
        <div className="block-head">
          <span className="uppercase-mini" style={{ color: "var(--warn)" }}>
            // LEGACY-STAMMDATEN ERKANNT
          </span>
        </div>
        <div className="notice-body">
          <div>
            Dieser Tenant nutzt noch die Insel-Variante{" "}
            <code
              className="mono"
              style={{
                background: "var(--bg-elevated)",
                padding: "2px 6px",
                borderRadius: 4,
              }}
            >
              kundenartikel.json
            </code>
            . Mit einem Klick migrieren wir die Artikel in den globalen
            Baustoff-Katalog und legen ein Tenant-Sortiment mit Overrides an.
          </div>
          {error && (
            <div
              className="warn-line"
              style={{
                borderLeftColor: "var(--bad)",
                background: "oklch(0.72 0.18 25 / 0.08)",
              }}
            >
              <strong>Fehler:</strong> <span className="mono">{error}</span>
            </div>
          )}
          <div>
            <button
              type="button"
              className="btn primary"
              onClick={handleMigrate}
              disabled={migrating}
            >
              {migrating ? "Migriere …" : "In Catalog migrieren"}
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (sortiment) {
    return (
      <section
        className="block"
        style={{
          borderColor: "oklch(0.78 0.16 152 / 0.4)",
          marginBottom: 14,
        }}
      >
        <div className="block-head">
          <span className="uppercase-mini" style={{ color: "var(--ok)" }}>
            // CATALOG AKTIV
          </span>
        </div>
        <div className="notice-body">
          <div>
            <span className="mono" style={{ color: "var(--fg)" }}>
              {effective.length}
            </span>{" "}
            Artikel im Tenant-Sortiment, gespeist aus dem globalen Baustoff-Stamm.
            {orphanRefs.length > 0 && (
              <span
                className="pill bad"
                style={{ marginLeft: 8, fontSize: 10 }}
              >
                {orphanRefs.length} verwaiste Refs
              </span>
            )}
            {result && (
              <span
                className="mono dim"
                style={{ marginLeft: 8, fontSize: 11 }}
              >
                · zuletzt migriert: +{result.catalogAdded} neu, ↻{" "}
                {result.catalogReused} wiederverwendet
              </span>
            )}
          </div>
        </div>
      </section>
    );
  }

  return null;
}
